import { inject } from '@angular/core';
import { Router, CanActivateFn, CanActivateChildFn } from '@angular/router';
import { RoleService } from './RoleService';
import { OAuthService } from 'angular-oauth2-oidc';

export const AdminRoleGuard: CanActivateFn | CanActivateChildFn = async (route, state) =>
{
    const router: Router = inject(Router);
    const roleService: RoleService = inject(RoleService);
    const oauthService: OAuthService = inject(OAuthService);

    // Process the discovery document and, if we have just come back from the identity
    // server, exchange the authorization code for tokens. Wrapped in try/catch so a
    // transport failure (e.g. an untrusted https://localhost dev certificate blocking the
    // token request) is surfaced instead of silently looping.
    try
    {
        await oauthService.loadDiscoveryDocumentAndTryLogin();
    }
    catch (err)
    {
        console.error(
            '[AdminRoleGuard] OIDC discovery or token exchange failed. If the API runs on ' +
            'https://localhost with the ASP.NET dev certificate, trust it with ' +
            '`dotnet dev-certs https --trust`, then clear site data and sign in again.', err);
    }

    if (oauthService.hasValidAccessToken())
    {
        roleService.initData();

        if (roleService.hasRole('admin') || roleService.hasRole('employee'))
        {
            return true;
        }

        // Authenticated but not permitted for the admin area.
        router.navigateByUrl('/unauthorized');
        return false;
    }

    // No valid token. If the URL still carries an authorization `code`, we just returned
    // from the identity server and the code exchange failed. Re-starting the login flow here
    // would send us back to the IdP (which still has a valid cookie) and bounce straight back
    // to `/?code=...` — an infinite dashboard <-> code redirect loop. Stop instead.
    const hasAuthCode =
        route.queryParamMap.has('code') ||
        new URLSearchParams(window.location.search).has('code');

    if (hasAuthCode)
    {
        console.error(
            '[AdminRoleGuard] Returned from the identity server with an authorization code but no ' +
            'token was obtained (the code exchange failed). Not restarting login, to avoid a redirect loop. ' +
            'Check the Network tab for the failing /connect/token request.');
        router.navigateByUrl('/unauthorized');
        return false;
    }

    // Genuinely unauthenticated and no code in the URL: start the login flow once.
    oauthService.initCodeFlow();
    return false;
};
